import { ArticleSearchObject } from '~/services/search/config/articles/types';
import { EventSearchObject } from '~/services/search/config/events/types';
import { ProductSearchObject } from '~/services/search/config/products/types';
import { ResourceSearchObject } from '~/services/search/config/resources/types';

import { ProviderSearchResults } from '../types';
import { RESULTS_LIMIT } from './consts';
import { initializeSearch } from './initializeSearch';
import {
  ArticleSearchRawObject,
  EventSearchRawObject,
  ProductSearchRawObject,
  ResourceSearchRawObject,
} from './types';

const isArticleSearchDocument = (
  searchResult:
    | ArticleSearchRawObject
    | ProductSearchRawObject
    | ResourceSearchRawObject
    | EventSearchRawObject
): searchResult is ArticleSearchRawObject =>
  searchResult.meta.type === 'article';

const isProductSearchDocument = (
  searchResult:
    | ArticleSearchRawObject
    | ProductSearchRawObject
    | ResourceSearchRawObject
    | EventSearchRawObject
): searchResult is ProductSearchRawObject =>
  searchResult.meta.type === 'product';

const isResourceSearchDocument = (
  searchResult:
    | ResourceSearchRawObject
    | ProductSearchRawObject
    | ArticleSearchRawObject
    | EventSearchRawObject
): searchResult is ResourceSearchRawObject =>
  searchResult.meta.type === 'resource';

const isEventSearchDocument = (
  searchResult:
    | EventSearchRawObject
    | ArticleSearchRawObject
    | ProductSearchRawObject
    | ResourceSearchRawObject
): searchResult is EventSearchRawObject => searchResult.meta.type === 'event';

export const executeSearch = async (
  searchQuery: string,
  locale: string
): Promise<ProviderSearchResults> => {
  await initializeSearch(locale);

  const pagefind = await import('static/pagefind/pagefind');
  const search = await pagefind.search(searchQuery);
  const searchResult = await Promise.all(
    search.results.slice(0, RESULTS_LIMIT).map((result) => result.data())
  );

  const articles: Array<ArticleSearchObject> = [];
  const products: Array<ProductSearchObject> = [];
  const resources: Array<ResourceSearchObject> = [];
  const events: Array<EventSearchObject> = [];

  searchResult?.forEach((data) => {
    if (isResourceSearchDocument(data)) {
      const result: ResourceSearchObject = {
        url: data.meta.url,
        relatedPageUid: data.meta.relatedPageUid,
        updatedAt: data.meta.updatedAt,
        title: data.meta.title,
        description: data.meta.description,
        seo_description: data.meta.seo_description,
        seo_keywords: JSON.parse(data.meta.seo_keywords),
        seo_title: data.meta.seo_title,
        id: data.meta.id,
        lang: data.meta.lang,
        type: 'resource',
        image: data.meta.image,
        categories:
          typeof data.meta.categories === 'string'
            ? JSON.parse(data.meta.categories)
            : data.meta.categories || [],
        pageAdditionalData: JSON.parse(data.meta.pageAdditionalData),
        label:
          typeof data.meta.label === 'string'
            ? JSON.parse(data.meta.label)
            : data.meta.label || [],
      };
      resources.push(result);
    }

    if (isEventSearchDocument(data)) {
      const result: EventSearchObject = {
        url: data.meta.url,
        relatedPageUid: data.meta.relatedPageUid,
        updatedAt: data.meta.updatedAt,
        title: data.meta.title,
        description: data.meta.description,
        seo_description: data.meta.seo_description,
        seo_keywords: JSON.parse(data.meta.seo_keywords),
        seo_title: data.meta.seo_title,
        id: data.meta.id,
        lang: data.meta.lang,
        type: 'event',
        image: data.meta.image,
        categories:
          typeof data.meta.categories === 'string'
            ? JSON.parse(data.meta.categories)
            : data.meta.categories || [],
        eventTimestamp: JSON.parse(data.meta.eventTimestamp),
        label:
          typeof data.meta.label === 'string'
            ? JSON.parse(data.meta.label)
            : data.meta.label || [],
      };
      events.push(result);
    }

    if (isArticleSearchDocument(data)) {
      const result: ArticleSearchObject = {
        url: data.meta.url,
        title: data.meta.title,
        description: data.meta.description,
        seo_description: data.meta.seo_description,
        seo_keywords: JSON.parse(data.meta.seo_keywords),
        seo_title: data.meta.seo_title,
        id: data.meta.id,
        lang: data.meta.lang,
        type: 'article',
        image: data.meta.image,
        categories:
          typeof data.meta.categories === 'string'
            ? JSON.parse(data.meta.categories)
            : data.meta.categories || [],
        label:
          typeof data.meta.label === 'string'
            ? JSON.parse(data.meta.label)
            : data.meta.label || [],
      };
      articles.push(result);
    }

    if (isProductSearchDocument(data)) {
      const result: ProductSearchObject = {
        id: data.meta.id,
        relatedPageUid: data.meta.relatedPageUid,
        product_brand_name: data.meta.product_brand_name,
        url: data.meta.url,
        title: data.meta.title,
        description: data.meta.description,
        seo_description: data.meta.seo_description,
        seo_keywords: JSON.parse(data.meta.seo_keywords),
        seo_title: data.meta.seo_title,
        sku: data.meta.sku,
        lang: data.meta.lang,
        image_data: data.meta.image_data,
        image_title: data.meta.image_title,
        image_url: data.meta.image_url,
        aroma_scent: data.meta.aroma_scent,
        net_content_statement: data.meta.aroma_scent,
        variant_and_icon: data.meta.variant_and_icon,
        brand_name: JSON.parse(data.meta.brand_name),
        barcode_number: data.meta.barcode_number,
        segment_description: data.meta.segment_description,
        categories_title: data.meta.categories_title,
        type: 'product',
      };
      products.push(result);
    }
  });

  return {
    articles,
    products,
    resources,
    events,
  };
};
